import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { CardBody } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import AccountModalTab from '../../../../types/AccountModalTab';
import FormFieldId from '../../../../types/FormFieldId';
import { labels } from '../../../../util/formLabels';
import {
  isEmail,
  maxLength,
  minLength,
  notBlank,
} from '../../../../util/formValidators';
import safeCloneDeep from '../../../../util/safeCloneDeep';
import ButtonLink from '../../../common/ButtonLink';
import CheckoutCustomerInfoForm from '../CheckoutCustomerInfoForm';

const CheckoutCustomerInfo = ({
  accountStore,
  checkoutStore,
  onOrderUpdate,
}) => {
  const { order } = checkoutStore;

  const form = useMemo(() => {
    const billingEmailField = order.getCustomerField(
      FormFieldId.BILLING_EMAIL
    ).field;

    const billingNewsletterField = order.getCustomerField(
      FormFieldId.NEWSLETTER_SUBSCRIPTION
    ).field;

    const billingEmailFieldLabel = labels.email;
    const emailValidators = [isEmail(billingEmailFieldLabel)];

    if (billingEmailField.required) {
      emailValidators.push(notBlank(billingEmailFieldLabel));
    }

    if (billingEmailField.min_length) {
      emailValidators.push(
        minLength(billingEmailFieldLabel, billingEmailField.min_length)
      );
    }

    if (billingEmailField.max_length) {
      emailValidators.push(
        maxLength(billingEmailFieldLabel, billingEmailField.max_length)
      );
    }

    const formFields = {
      [FormFieldId.BILLING_EMAIL]: createFormField(
        safeCloneDeep(billingEmailField),
        emailValidators
      ),
      [FormFieldId.NEWSLETTER_SUBSCRIPTION]: createFormField(
        safeCloneDeep(billingNewsletterField),
        [],
        () => !!billingNewsletterField
      ),
    };

    return createForm(formFields, 'CustomerInfoForm');
  }, [
    order.getCustomerField(FormFieldId.BILLING_EMAIL).value,
    order.getCustomerField(FormFieldId.NEWSLETTER_SUBSCRIPTION).value,
  ]);

  const handleUpdate = (args) => {
    onOrderUpdate(args);
  };

  return (
    <CardBody
      className={classNames(
        'CheckoutCustomerInfo',
        'CheckoutCustomerInfo__body'
      )}
    >
      <p>
        <FormattedMessage
          id="checkout.customerInfo.loginInfo"
          defaultMessage="Please enter your email address below so we can send you an order confirmation. If you are already a member, please {login}."
          values={{
            login: (
              <ButtonLink
                className="CheckoutCustomerInfo_login-link"
                onClick={() => {
                  accountStore.accountModal.setTab(AccountModalTab.LOGIN);
                  accountStore.accountModal.toggle(true);
                }}
              >
                <FormattedMessage {...globalTranslations.logInSentence} />
              </ButtonLink>
            ),
          }}
        />
      </p>
      <CheckoutCustomerInfoForm form={form} onOrderUpdate={handleUpdate} />
    </CardBody>
  );
};

CheckoutCustomerInfo.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  orderNumber: PropTypes.number.isRequired,
};

export default inject(
  'accountStore',
  'checkoutStore'
)(observer(CheckoutCustomerInfo));
